import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Tabs,
  Tab,
  Box,
  Typography,
  CircularProgress,
  Paper,
  useTheme,
  Grid,
  alpha,
  Chip,
  Avatar,
  Tooltip,
  Dialog as ConfirmDialog,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import firebase from '../utilities/firebase';
import { StateManager } from '../utilities/stateManager';
import Store from '../utilities/store';
import constants from '../utilities/constants';
import { useAutoZ, ModalStackProvider } from '../utilities/ModalStackContext';
import { useTallDialogSizing } from '../utilities/dialogSizing';
import moment from 'moment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import algolia from '../utilities/algolia';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { v4 as uuidv4 } from 'uuid';
import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import LaunchIcon from '@mui/icons-material/Launch';
import PrintIcon from '@mui/icons-material/Print';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DrawOutlinedIcon from '@mui/icons-material/DrawOutlined';

// Import tab components
import EstimateTab from './ServiceOrderModal/EstimateTab';
import ServicesTab from './ServiceOrderModal/ServicesTab';
import PartsTab from './ServiceOrderModal/PartsTab';
import SubcontractTab from './ServiceOrderModal/SubcontractTab';
import AccountingTab from './ServiceOrderModal/AccountingTab';
import NotesTab from './ServiceOrderModal/NotesTab';
import TimeTab from './ServiceOrderModal/TimeTab';
import FilesTab from './ServiceOrderModal/FilesTab';
import QualityTab, { computeQualitySummary } from './ServiceOrderModal/QualityTab';
import SendsTab from './ServiceOrderModal/SendsTab';
import ChatTab from './ServiceOrderModal/ChatTab';
import NewCustomerModal from './NewCustomerModal';
import ShippingStatusChip from './ShippingStatusChip';
import SalesCreditDialog from './SalesCreditDialog';
import { creditsLabel, resolveOrderCredits, CREDIT_SOURCE } from '../utilities/salesCredits';

const getDocData = doc => {
  return {id: doc.id, ...doc.data()}
}

export default function ServiceOrderModal({ open, onClose, stockNumber, allowedTabs = null, hideEstimate = false, readOnly = false }) {
  const myZ = useAutoZ(open, stockNumber);
  const childZ = myZ + 10;
  const theme = useTheme();
  const { fullScreen, paperSx } = useTallDialogSizing({ height: '90vh', margin: 4 });
  const [order, setOrder] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const [activeTab, setActiveTab] = React.useState(0);
  const [allDataLoaded, setAllDataLoaded] = React.useState(false);
  // Creator name resolved from the `users` collection for orders that lack a stored
  // createdByName and whose creator id isn't in the constants.userNames map.
  const [resolvedCreator, setResolvedCreator] = React.useState(null);
  // Quick car-thumbnail capture (for imageless cars) straight from the SO.
  const [uploadingThumb, setUploadingThumb] = React.useState(false);
  const thumbInputRef = React.useRef(null);

  // Track loaded car ID to prevent unnecessary reloads
  const loadedCarIdRef = React.useRef(null);
  // Track last stockNumber to detect when it changes
  const lastStockNumberRef = React.useRef(null);
  // The refresh button bumps this to reload the modal's data without reloading the page.
  const [refreshKey, setRefreshKey] = React.useState(0);

  // Track which data has been loaded
  const [dataLoaded, setDataLoaded] = React.useState({
    order: false,
    customer: false,
    car: false,
    services: false,
    parts: false,
    subcontracts: false,
    deposits: false,
    expenses: false,
    inspections: false,
    files: false,
  });

  // Resolve the creator's name for display. Prefer the name stored on the order
  // (createdByName) and the hardcoded constants.userNames map; only fall back to a
  // live users-collection lookup for older orders / ids missing from both.
  React.useEffect(() => {
    setResolvedCreator(null);
    const id = order.createdBy;
    if (!id || order.createdByName || constants.userNames[id]) return;
    let active = true;
    Store.getUserName(id).then((name) => {
      if (active && name) setResolvedCreator(name);
    });
    return () => { active = false; };
  }, [order.createdBy, order.createdByName]);

  React.useEffect(() => {
    // Reset ref when modal closes
    if (!open) {
      lastStockNumberRef.current = null;
      return;
    }
    
    if (!stockNumber) {
      return;
    }

    // Reset state when modal opens or stockNumber changes (only on initial open, not on updates)
    const stockNumberChanged = lastStockNumberRef.current !== stockNumber;
    if (stockNumberChanged) {
      lastStockNumberRef.current = stockNumber;
      setOrder({});
      setLoading(true);
      setAllDataLoaded(false);
      loadedCarIdRef.current = null;
      setDataLoaded({
        order: false,
        customer: false,
        car: false,
        services: false,
        parts: false,
        subcontracts: false,
        deposits: false,
        expenses: false,
        inspections: false,
        files: false,
      });
    } else {
      // Refresh: keep the current data on screen, but fetch the car again.
      loadedCarIdRef.current = null;
    }

    const db = firebase.firestore();
    const unsubscribeFunctions = [];

    // Set up realtime listener for inspections
    const inspectionsUnsubscribe = db.collection('inspections').where('stock', '==', stockNumber).onSnapshot((snap) => {
      const inspections = snap.docs.map(getDocData);
      setOrder(prevOrder => ({
        ...prevOrder,
        inspections: inspections,
        inspections_loaded: true
      }));
      setDataLoaded(prev => ({...prev, inspections: true}));
    });
    unsubscribeFunctions.push(inspectionsUnsubscribe);

    // Set up realtime listener for parts
    const partsUnsubscribe = db.collection('parts').where('order', '==', stockNumber).onSnapshot((partsSnap) => {
      const parts = partsSnap.docs.map(getDocData);
      setOrder(prevOrder => ({
        ...prevOrder,
        parts: parts,
        parts_loaded: true
      }));
      setDataLoaded(prev => ({...prev, parts: true}));
    });
    unsubscribeFunctions.push(partsUnsubscribe);

    // Set up realtime listener for subcontracted work
    const subcontractsUnsubscribe = db.collection('subcontracts').where('order', '==', stockNumber).onSnapshot((subSnap) => {
      const subcontracts = subSnap.docs.map(getDocData);
      setOrder(prevOrder => ({
        ...prevOrder,
        subcontracts: subcontracts,
        subcontracts_loaded: true
      }));
      setDataLoaded(prev => ({...prev, subcontracts: true}));
    });
    unsubscribeFunctions.push(subcontractsUnsubscribe);

    // Set up realtime listener for services
    const servicesUnsubscribe = db.collection('services').where('order', '==', stockNumber).onSnapshot((servicesSnap) => {
      const services = servicesSnap.docs.map(getDocData);
      setOrder(prevOrder => ({
        ...prevOrder,
        services: services,
        services_loaded: true
      }));
      setDataLoaded(prev => ({...prev, services: true}));
    });
    unsubscribeFunctions.push(servicesUnsubscribe);

    // Set up realtime listener for quality checks
    const qualityChecksUnsubscribe = db.collection('qualityChecks').where('order', '==', stockNumber).onSnapshot((qcSnap) => {
      const qualityChecks = qcSnap.docs.map(getDocData);
      setOrder(prevOrder => ({
        ...prevOrder,
        qualityChecks: qualityChecks,
        qualityChecks_loaded: true
      }));
      setDataLoaded(prev => ({...prev, qualityChecks: true}));
    });
    unsubscribeFunctions.push(qualityChecksUnsubscribe);

    // Set up realtime listener for files
    const filesUnsubscribe = db.collection('files').where('stock', '==', stockNumber).onSnapshot((fileSnap) => {
      const files = fileSnap.docs.map(getDocData);
      setOrder(prevOrder => ({
        ...prevOrder,
        files: files,
        files_loaded: true
      }));
      setDataLoaded(prev => ({...prev, files: true}));
    });
    unsubscribeFunctions.push(filesUnsubscribe);

    // Send history for this order: every estimate/invoice emailed or sent for
    // signature. One listener serves both the Sends tab and the header chip, so
    // neither subscribes on its own.
    const documentSendsUnsubscribe = Store.subscribeServiceDocumentSends(stockNumber, (documentSends) => {
      setOrder(prevOrder => ({
        ...prevOrder,
        documentSends: documentSends,
        documentSends_loaded: true
      }));
    });
    unsubscribeFunctions.push(documentSendsUnsubscribe);

    // Set up realtime listener for deposits
    const depositsUnsubscribe = db.collection('deposits').where('stock', '==', stockNumber).onSnapshot((transactionSnapshot) => {
      const deposits = transactionSnapshot.docs.map(getDocData).filter(x => x.type === "service");
      setOrder(prevOrder => ({
        ...prevOrder,
        deposits: deposits,
        deposits_loaded: true
      }));
      setDataLoaded(prev => ({...prev, deposits: true}));
    });
    unsubscribeFunctions.push(depositsUnsubscribe);

    // Set up realtime listener for purchases/expenses
    const purchasesUnsubscribe = db.collection('purchases').where('stock', '==', stockNumber).onSnapshot((expenseSnap) => {
      const expenses = expenseSnap.docs.map(getDocData);
      setOrder(prevOrder => ({
        ...prevOrder,
        expenses: expenses,
        expenses_loaded: true
      }));
      setDataLoaded(prev => ({...prev, expenses: true}));
    });
    unsubscribeFunctions.push(purchasesUnsubscribe);

    // Set up realtime listener for main order document
    const orderUnsubscribe = db.doc('orders/'+stockNumber).onSnapshot((doc) => {
      if(doc.exists){
        let data = doc.data();
        data.disabled = !StateManager.isAdmin() && data.status === constants.order_statuses.at(-1);
        data.order_loaded = true;
        
        // Remove collection references that should not be stored in the order document
        // These are queried separately from their respective collections
        const fieldsToRemove = ['parts', 'subcontracts', 'services', 'deposits', 'expenses', 'files', 'inspections',
                                'parts_loaded', 'subcontracts_loaded', 'services_loaded', 'deposits_loaded', 'expenses_loaded',
                                'files_loaded', 'inspections_loaded', 'documentSends', 'documentSends_loaded'];
        const hasCollectionData = fieldsToRemove.some(field => data.hasOwnProperty(field));
        
        if (hasCollectionData) {
          // Remove these fields and save the cleaned document back (one-time fix)
          const cleanedData = {};
          fieldsToRemove.forEach(field => {
            if (data.hasOwnProperty(field)) {
              delete data[field];
              cleanedData[field] = firebase.firestore.FieldValue.delete();
            }
          });
          if (Object.keys(cleanedData).length > 0) {
            db.doc('orders/'+stockNumber).set(cleanedData, {merge: true}).catch(err => {
              console.error('Error removing collection data from order document:', err);
            });
          }
        }
        
        // Normalize car and customer to IDs only (extract ID if they're objects)
        // This ensures we never store full objects in the database
        let carId = data.car;
        if (carId && typeof carId === 'object' && carId.id) {
          carId = carId.id;
          // If database has full object, save just the ID back (one-time fix)
          db.doc('orders/'+stockNumber).set({car: carId}, {merge: true}).catch(err => {
            console.error('Error normalizing car ID:', err);
          });
        } else if (carId && typeof carId !== 'string') {
          // Handle edge case where car might be null or unexpected type
          carId = null;
        }
        
        let customerId = data.customer;
        if (customerId && typeof customerId === 'object' && customerId.id) {
          customerId = customerId.id;
          // If database has full object, save just the ID back (one-time fix)
          db.doc('orders/'+stockNumber).set({customer: customerId}, {merge: true}).catch(err => {
            console.error('Error normalizing customer ID:', err);
          });
        } else if (customerId && typeof customerId !== 'string') {
          // Handle edge case where customer might be null or unexpected type
          customerId = null;
        }
        
        // Update data with normalized IDs (always use string IDs, never objects)
        data.car = carId || null;
        data.customer = customerId || null;
        
        // Preserve existing nested objects (car, customer, etc.) when updating
        setOrder(prevOrder => {
          const updated = {...prevOrder, ...data};
          // Preserve car object if it exists and car ID hasn't changed
          if (prevOrder.car && typeof prevOrder.car === 'object' && prevOrder.car.id === data.car) {
            updated.car = prevOrder.car;
          }
          // Preserve customer object if it exists and customer ID hasn't changed
          if (prevOrder.customer && typeof prevOrder.customer === 'object' && 
              (prevOrder.customer.id === data.customer || 
               (typeof data.customer === 'string' && prevOrder.customer.id === data.customer))) {
            updated.customer = prevOrder.customer;
          }
          // Preserve thumbnail if it exists
          if (prevOrder.thumbnail && !data.thumbnail) {
            updated.thumbnail = prevOrder.thumbnail;
          }
          return updated;
        });
        setDataLoaded(prev => ({...prev, order: true}));

        // Load car data once if exists and not already loaded
        if(carId && loadedCarIdRef.current !== carId){
          loadedCarIdRef.current = carId;
          db.doc('cars/'+carId).get().then((car_data) => {
            if(car_data.exists) {
              const carData = {...car_data.data(), id: carId};
              carData.title = `${carData.stock || ""} - ${carData.year || ""} ${carData.make || ""} ${carData.model || ""}`;
              setOrder(prevOrder => ({
                ...prevOrder,
                car: carData,
                car_loaded: true
              }));
              setDataLoaded(prev => ({...prev, car: true}));
            } else {
              setOrder(prevOrder => ({...prevOrder, car_loaded: true}));
              setDataLoaded(prev => ({...prev, car: true}));
            }
          }).catch((error) => {
            console.error('Error loading car data:', error);
            setOrder(prevOrder => ({...prevOrder, car_loaded: true}));
            setDataLoaded(prev => ({...prev, car: true}));
          });
        } else if (!carId) {
          loadedCarIdRef.current = null;
          setOrder(prevOrder => ({...prevOrder, car_loaded: true}));
          setDataLoaded(prev => ({...prev, car: true}));
        } else {
          // Car already loaded, just mark as loaded
          setDataLoaded(prev => ({...prev, car: true}));
        }

        // Customer data is handled separately
        setDataLoaded(prev => ({...prev, customer: true}));

      } else {
        StateManager.setAlertAndOpen('Service order not found', 'error');
        onClose();
      }
    });
    unsubscribeFunctions.push(orderUnsubscribe);

    // Cleanup function to unsubscribe from all listeners
    return () => {
      unsubscribeFunctions.forEach(unsubscribe => unsubscribe());
    };
  }, [open, stockNumber, refreshKey]); // Removed onClose to prevent unnecessary re-runs

  React.useEffect(() => {
    if (!open || !stockNumber) return;
    const car = order.car || {};
    const carInfo = car.title || `${car.stock || ''} ${car.year || ''} ${car.make || ''} ${car.model || ''}`.replace(/\s+/g, ' ').trim();
    document.title = carInfo ? `${stockNumber} - ${carInfo}` : `${stockNumber}`;
    return () => { document.title = StateManager.title || document.title; };
  }, [open, stockNumber, order.car]);

  // The deal doc (deals/{stock}) is fetched for two reasons: the shipping chip needs it
  // for the legacy shipping_complete fallback — matching the car modal so the SN and SO
  // chips never disagree — and the rep who sold the car is shown on the order as its own
  // fact. It is NOT the sales credit for the work. Consignment rep (car.consign_rep) is
  // already on hand.
  const [deal, setDeal] = React.useState(null);
  React.useEffect(() => {
    const stock = order.car && order.car.stock;
    if (!stock) { setDeal(null); return; }
    let active = true;
    firebase.firestore().doc('deals/' + stock).get()
      .then(doc => {
        if (!active) return;
        setDeal(doc.exists ? doc.data() : null);
      })
      .catch(() => { if (active) setDeal(null); });
    return () => { active = false; };
  }, [order.car && order.car.stock, refreshKey]);

  // Who gets paid for this SO. Only a credit recorded on the order counts — the rep who
  // sold the car is shown separately and earns nothing here unless somebody credits them.
  const salesCredits = React.useMemo(() => resolveOrderCredits(order), [order]);
  const salesCreditIsExplicit = Array.isArray(order.sales_credits);
  // Who sold the car. Shown for context, never mistaken for the credit.
  const carSalesRep = (deal && deal.sales_rep) || '';
  const salesCreditLocked = !!order.sales_credits_locked;
  // Nobody has decided yet, so anyone working the order can say who earned it. Once a
  // credit is on the order it is a pay decision, and only the back office moves it.
  // A generated pay period locks it outright so a paid period can never change shape.
  const canEditSalesCredit = !order.disabled && !readOnly && !salesCreditLocked
    && (!salesCreditIsExplicit || StateManager.isAdmin() || StateManager.isManager()
      || StateManager.isBackoffice());

  // Set up customer listener that updates when customer ID changes
  React.useEffect(() => {
    let customerId = null;
    
    // Determine customer ID from different possible formats
    if (typeof order.customer === 'string' && order.customer) {
      customerId = order.customer;
    } else if (order.customer && order.customer.id) {
      customerId = order.customer.id;
    }
    
    if (customerId) {
      const customerUnsubscribe = firebase.firestore().doc('customers/'+customerId).onSnapshot((cust_data) => {
        if(cust_data.exists) {
          const customerData = {...cust_data.data(), id: customerId};
          customerData.display_name = `${customerData.first_name || ""}${!!customerData.last_name ? " " : ""}${customerData.last_name || ""}`;
          setOrder(prevOrder => ({
            ...prevOrder,
            customer: customerData
          }));
        }
      });
      return () => customerUnsubscribe();
    }
  }, [order.customer]);

  // Update loading state when all critical data is loaded
  React.useEffect(() => {
    const criticalDataLoaded = 
      dataLoaded.order && 
      dataLoaded.customer && 
      dataLoaded.car && 
      dataLoaded.services &&
      dataLoaded.parts &&
      dataLoaded.subcontracts &&
      dataLoaded.deposits &&
      dataLoaded.expenses;
    
    if (criticalDataLoaded && loading) {
      setLoading(false);
      setAllDataLoaded(true);
    }
  }, [dataLoaded, loading]);

  // Update order state manager
  React.useEffect(() => {
    StateManager.updateOrder = (data) => setOrder(prevOrder => ({...prevOrder, ...data}));
    StateManager.updateCar = (data) => setOrder(prevOrder => ({...prevOrder, ...data}));
  }, []);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Upload a photo straight from the SO and set it as the car's thumbnail —
  // handy for cars that don't have an image yet. Only allowed while the car is
  // still in its early photo phase (see canEditThumbnail in the render body).
  const handleThumbnailFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    const carObj = order.car || {};
    const stock = carObj.stock || carObj.id;
    if (!file || !stock) return;
    const activeIdx = constants.statuses.indexOf('active');
    const picturesIdx = constants.sub_statuses.indexOf('pictures');
    const statusIdx = constants.statuses.indexOf((carObj.status || '').toLowerCase());
    const subIdx = constants.sub_statuses.indexOf((carObj.sub_status || '').toLowerCase());
    if ((statusIdx !== -1 && statusIdx >= activeIdx) || subIdx > picturesIdx) {
      StateManager.setAlertAndOpen('Car photo can only be set before the car goes active / past the pictures stage', 'warning');
      return;
    }
    setUploadingThumb(true);
    try {
      const url = await Store.setCarThumbnailFromFile(stock, file);
      if (url) {
        setOrder(prev => ({ ...prev, car: { ...(prev.car || {}), thumbnail: url } }));
        StateManager.setAlertAndOpen('Car photo added', 'success');
      }
    } catch (err) {
      StateManager.setAlertAndOpen('Failed to upload photo', 'error');
    } finally {
      setUploadingThumb(false);
    }
  };

  const allTabs = [
    { label: 'Accounting', component: AccountingTab },
    { label: 'Estimate', component: EstimateTab },
    // Sits next to Estimate on purpose: build the document, then track it.
    { label: 'Sends', component: SendsTab },
    { label: 'Services', component: ServicesTab },
    { label: 'Parts', component: PartsTab },
    { label: 'Subcontract', component: SubcontractTab },
    { label: 'Quality', component: QualityTab },
    { label: 'Notes', component: NotesTab },
    { label: 'Files', component: FilesTab },
    // AI chat about this SO — read-only, reads services/parts/quality/time/notes.
    { label: 'Chat', component: ChatTab },
    // Time entry editor — admin/manager only.
    ...((StateManager.isAdmin() || StateManager.isManager())
      ? [{ label: 'Time', component: TimeTab }]
      : []),
  ];

  const tabs = allowedTabs ? allTabs.filter(t => allowedTabs.includes(t.label)) : allTabs;

  const ActiveTabComponent = tabs[activeTab]?.component;

  // Newest send that actually asked for a signature. Email-only sends never set
  // the header chip — nobody was asked to sign, so there is nothing to report.
  // The list arrives newest-first from Store.subscribeServiceDocumentSends.
  const signatureSend = React.useMemo(
    () => (order.documentSends || []).find(s => s.requiresSignature),
    [order.documentSends],
  );

  const sendsTabIndex = tabs.findIndex(t => t.label === 'Sends');

  // Helper functions for profile section
  const getStatusColor = (status) => {
    const statusIndex = constants.order_statuses.indexOf(status);
    if (statusIndex === -1) return theme.palette.grey[500];

    const totalStatuses = constants.order_statuses.length;
    const progress = statusIndex / (totalStatuses - 1);

    if (progress < 0.25) {
      return theme.palette.info.light;
    } else if (progress < 0.5) {
      return theme.palette.info.main;
    } else if (progress < 0.75) {
      return theme.palette.primary.main;
    } else {
      return theme.palette.success.main;
    }
  };

  const getMechanicColor = (mechanicId) => {
    if (!mechanicId) return theme.palette.grey[500];
    
    const colors = [
      theme.palette.primary.main,
      theme.palette.success.main,
      theme.palette.info.main,
      theme.palette.error.main,
      theme.palette.secondary.main,
      theme.palette.warning.dark,
      '#2E7D32',
      '#1976D2',
      '#7B1FA2',
      '#C2185B',
      '#00796B',
      '#5D4037',
      '#455A64',
      '#D81B60',
      '#00897B',
      '#5E35B1',
    ];

    const index = constants.mechanics.findIndex(m => m.id === mechanicId);
    if (index === -1) return theme.palette.grey[500];
    return colors[index % colors.length];
  };

  const getMechanicName = (mechanicId) => {
    if (!mechanicId) return 'Not assigned';
    const mechanic = constants.mechanics.find(m => m.id === mechanicId);
    return mechanic ? mechanic.name : 'Not assigned';
  };

  // Print function
  const handlePrint = async () => {
    const printWindow = window.open('', '_blank');
    const car = order.car || {};
    const customer = order.customer || {};
    const thumbnail = car.thumbnail || order.thumbnail || null;
    
    // Format car information
    const carInfo = car.title || `${car.stock || ''} - ${car.year || ''} ${car.make || ''} ${car.model || ''}`.trim();
    const carDetails = [
      car.vin ? `VIN: ${car.vin}` : '',
      car.mileage ? `Mileage: ${car.mileage}` : ''
    ].filter(Boolean);

    // Format customer information
    const customerInfo = customer.display_name || 
      (customer.first_name || customer.last_name 
        ? `${customer.first_name || ''} ${customer.last_name || ''}`.trim() 
        : 'Not assigned');
    const customerPhone = customer.phone || customer.phone_number || '';
    const customerDetails = [
      customer.email ? `Email: ${customer.email}` : '',
      customerPhone ? `Phone: ${customerPhone}` : ''
    ].filter(Boolean);

    // For internal print, list outstanding (non-complete) items first and push
    // completed ones to the bottom. Stable so original order is otherwise kept.
    const incompleteFirst = (a, b) => (a.status === 'complete' ? 1 : 0) - (b.status === 'complete' ? 1 : 0);
    // Parts/subcontracts: declined items go all the way to the bottom; within
    // that, outstanding before complete.
    const declinedLast = (a, b) =>
      (a.declined ? 1 : 0) - (b.declined ? 1 : 0) ||
      (a.status === 'complete' ? 1 : 0) - (b.status === 'complete' ? 1 : 0);

    // Format services list — always grouped by tag for the internal print.
    const services = [...(order.services || [])].sort(incompleteFirst);
    const serviceRowHtml = (service) => {
      const hours = service.time || 0;
      if (service.declined) {
        return `<tr><td><span style="text-decoration:line-through;color:#999">${service.name || 'Unnamed Service'}</span></td><td>${hours}</td><td style="color:#c62828;font-weight:bold">Declined</td></tr>`;
      }
      const status = service.status ? service.status.charAt(0).toUpperCase() + service.status.slice(1) : 'Pending';
      return `<tr><td>${service.name || 'Unnamed Service'}</td><td>${hours}</td><td>${status}</td></tr>`;
    };
    let servicesList;
    if (services.length === 0) {
      servicesList = '<tr><td colspan="3" class="no-data">No services found</td></tr>';
    } else {
      // Group by tag (preserving first-seen order), with a subheader per group.
      const groups = [];
      const idx = {};
      for (const s of services) {
        const tag = (s.tag && s.tag.trim()) || 'Other';
        if (idx[tag] === undefined) { idx[tag] = groups.length; groups.push({ tag, items: [] }); }
        groups[idx[tag]].items.push(s);
      }
      servicesList = groups.map((g) =>
        `<tr><td colspan="3" style="font-weight:bold;background:#f0f0f0;">${g.tag}</td></tr>` +
        g.items.map(serviceRowHtml).join('')
      ).join('');
    }

    // Format parts list — outstanding first, complete next, declined at the bottom.
    const parts = [...(order.parts || [])].sort(declinedLast);
    const partsList = parts.length > 0
      ? parts.map((part) => {
          const status = part.declined
            ? 'Declined'
            : (part.status ? part.status.charAt(0).toUpperCase() + part.status.slice(1) : 'Pending');
          const rawName = part.name || part.description || part.part_number || 'Unnamed Part';
          const partName = part.declined ? `<span style="text-decoration:line-through;color:#999">${rawName}</span>` : rawName;
          const statusCell = part.declined ? `<td style="color:#c62828;font-weight:bold">${status}</td>` : `<td>${status}</td>`;

          // Format order date - handle both YYYY/MM/DD and YYYY-MM-DD formats
          let orderDate = '-';
          if (part.orderDate) {
            const dateStr = part.orderDate.replace(/-/g, '/');
            const parsed = moment(dateStr, ['YYYY/MM/DD', 'MM/DD/YYYY'], true);
            orderDate = parsed.isValid() ? parsed.format('MM/DD/YYYY') : part.orderDate;
          }

          // Format arrival date - handle both YYYY/MM/DD and YYYY-MM-DD formats
          let arrivalDate = '-';
          if (part.arrivalDate) {
            const dateStr = part.arrivalDate.replace(/-/g, '/');
            const parsed = moment(dateStr, ['YYYY/MM/DD', 'MM/DD/YYYY'], true);
            arrivalDate = parsed.isValid() ? parsed.format('MM/DD/YYYY') : part.arrivalDate;
          }

          const cost = part.cost ? '$' + parseFloat(part.cost).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-';
          return `<tr><td>${partName}</td>${statusCell}<td>${orderDate}</td><td>${arrivalDate}</td><td style="text-align:right">${cost}</td></tr>`;
        }).join('')
      : '<tr><td colspan="5" class="no-data">No parts found</td></tr>';

    // Format subcontracted work list — outstanding first, declined at the bottom.
    const subcontracts = [...(order.subcontracts || [])].sort(declinedLast);
    const subcontractsList = subcontracts.length > 0
      ? subcontracts.map((sub) => {
          const status = sub.declined ? 'Declined' : (sub.status === 'complete' ? 'Complete' : 'Pending');
          const rawName = sub.name || 'Unnamed';
          const name = sub.declined ? `<span style="text-decoration:line-through;color:#999">${rawName}</span>` : rawName;
          const statusCell = sub.declined ? `<td style="color:#c62828;font-weight:bold">${status}</td>` : `<td>${status}</td>`;
          const vendor = sub.vendor || '-';
          const cost = sub.cost ? '$' + parseFloat(sub.cost).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-';
          return `<tr><td>${name}</td><td>${vendor}</td>${statusCell}<td style="text-align:right">${cost}</td></tr>`;
        }).join('')
      : '<tr><td colspan="4" class="no-data">No subcontracted work found</td></tr>';

    // Format service order status
    const orderStatus = order.status ? order.status.charAt(0).toUpperCase() + order.status.slice(1) : 'Unknown';

    // Accounting figures — mirror the AccountingTab split-allocation model:
    // Customer Pays + Internal (Skyway) = Revenue; Revenue − Total Cost = Profit.
    const useSplitModel = !!(order.customerPays && order.customerPays.type);

    // Clocked labor lives in timeLogs (not on the order), so fetch it to match the tab.
    let printTimeLogs = [];
    try {
      const tlSnap = await firebase.firestore()
        .collection('timeLogs')
        .where('serviceOrderId', '==', stockNumber)
        .get();
      printTimeLogs = tlSnap.docs.map((d) => d.data());
    } catch (e) {
      console.error('Print: timeLogs fetch failed', e);
    }

    // Labor = service labor + clocked labor from timeLogs. Service labor is
    // deconflicted against clocked labor via constants.countedServiceLaborCost:
    // legacy "clock" lines and (post-cutoff) clock-in mechanics' completed lines
    // are excluded so their cost isn't double-counted against timeLogs.
    const printServiceLabor = (order.services || [])
      .reduce((a, c) => a + constants.countedServiceLaborCost(c), 0);
    const nowMs = Date.now();
    const printClockedLabor = printTimeLogs.reduce((acc, log) => {
      const startMs = log.startTime && log.startTime.toMillis ? log.startTime.toMillis() : null;
      const endMs = log.endTime && log.endTime.toMillis
        ? log.endTime.toMillis()
        : (log.status === 'active' ? nowMs : null);
      if (!startMs || !endMs || endMs <= startMs) return acc;
      return acc + ((endMs - startMs) / 3600000) * (Number(log.rate) || 0);
    }, 0);
    const printLabor = Math.round((printServiceLabor + printClockedLabor) * 100) / 100;

    // Purchases — exclude the auto "trade" doc for this SO in split mode (avoids double-count).
    const printPurchases = (order.expenses || [])
      .filter((e) => !(useSplitModel && e.account === 'trade' && e.id === stockNumber))
      .reduce((a, c) => a + (c.amount || 0), 0);
    const printTotalCost = Math.round((printLabor + printPurchases) * 100) / 100;

    // Customer / Internal split.
    const printCustomerAmount = (() => {
      const cp = order.customerPays;
      if (!cp || cp.value == null) return 0;
      if (cp.type === 'percent') return Math.round(printTotalCost * (Number(cp.value) || 0)) / 100;
      return Number(cp.value) || 0;
    })();
    const printInternalAmount = order.internalAmount != null
      ? Number(order.internalAmount)
      : Math.max(0, Math.round((printTotalCost - printCustomerAmount) * 100) / 100);
    const printRevenue = useSplitModel
      ? Math.round((printCustomerAmount + printInternalAmount) * 100) / 100
      : (order.revenue || 0);
    const printProfit = Math.round((printRevenue - printTotalCost) * 100) / 100;

    // Accounts receivable — only the customer-billed portion is owed.
    const printDeposits = (order.deposits || []).reduce((a, c) => a + (c.amount || 0), 0);
    const printSalesTax = order.salesTax || 0;
    const printBilled = useSplitModel ? printCustomerAmount : printRevenue;
    const printTotal = Math.round((printBilled + printSalesTax) * 100) / 100;
    const printAmountOwed = Math.round((printTotal - printDeposits) * 100) / 100;

    const fmtCurrency = (n) => '$' + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    // Deposits list for the receipt. Stripe-collected deposits carry the PaymentIntent
    // id (pi_…) as their memo — show a friendly label for those instead.
    const depositRows = (order.deposits || []).slice().sort((a, b) => {
      const parseDate = (d) => (d ? moment(d, ['YYYY/MM/DD', 'MM/DD/YYYY', 'YYYY-MM-DD'], true).valueOf() || 0 : 0);
      return parseDate(a.date) - parseDate(b.date);
    });
    const depositsList = depositRows.length > 0
      ? depositRows.map((d) => {
          const memo = (d.memo && /^pi_/.test(d.memo)) ? 'Customer Credit Card Payment' : (d.memo || '-');
          return `<tr><td>${d.date || '-'}</td><td>${memo}</td><td style="text-align:right">${fmtCurrency(d.amount || 0)}</td></tr>`;
        }).join('')
      : '<tr><td colspan="3" class="no-data">No deposits recorded</td></tr>';

    // Create print content
    const printContent = `
<!DOCTYPE html>
<html>
<head>
  <title>Service Order ${stockNumber}</title>
  <style>
    @media print {
      @page {
        margin: 0.5in;
      }
      body {
        margin: 0;
        padding: 0;
      }
    }
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    body {
      font-family: Arial, sans-serif;
      font-size: 14px;
      line-height: 1.5;
      color: #333;
      padding: 15px;
    }
    h1 {
      font-size: 22px;
      color: #1976d2;
      border-bottom: 2px solid #1976d2;
      padding-bottom: 5px;
      margin-bottom: 15px;
    }
    h2 {
      font-size: 17px;
      color: #555;
      margin-top: 15px;
      margin-bottom: 8px;
      border-bottom: 1px solid #ddd;
      padding-bottom: 3px;
    }
    .summary {
      display: flex;
      gap: 15px;
      margin-bottom: 15px;
      border: 1px solid #ddd;
      padding: 12px;
      border-radius: 4px;
      background: #fafafa;
    }
    .summary-image {
      flex-shrink: 0;
      width: 130px;
      height: 130px;
    }
    .summary-image img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border: 1px solid #ccc;
      border-radius: 4px;
    }
    .summary-info {
      flex: 1;
      display: flex;
      gap: 0;
      font-size: 13px;
    }
    .summary-col {
      flex: 1;
      padding: 0 12px;
      border-right: 1px solid #ddd;
    }
    .summary-col:first-child { padding-left: 0; }
    .summary-col:last-child { border-right: none; }
    .summary-row {
      display: flex;
      flex-direction: column;
      margin-bottom: 8px;
    }
    .summary-label {
      font-weight: bold;
      color: #888;
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    .summary-value {
      color: #222;
      font-size: 13px;
      margin-top: 1px;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 5px;
      font-size: 13px;
    }
    table th {
      background-color: #f5f5f5;
      border: 1px solid #ddd;
      padding: 7px 10px;
      text-align: left;
      font-weight: bold;
      color: #555;
    }
    table td {
      border: 1px solid #ddd;
      padding: 6px 10px;
    }
    .accounting-table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 8px;
      font-size: 14px;
    }
    .accounting-table td {
      padding: 8px 12px;
      border-bottom: 1px solid #eee;
    }
    .accounting-table .label {
      color: #555;
      width: 60%;
    }
    .accounting-table .amount {
      text-align: right;
      font-weight: 500;
    }
    .accounting-table .total-row td {
      border-top: 2px solid #333;
      border-bottom: 2px solid #333;
      font-weight: 700;
      font-size: 15px;
    }
    .profit-positive { color: #2e7d32; }
    .profit-negative { color: #c62828; }
    table tr:nth-child(even) {
      background-color: #fafafa;
    }
    tr, td, tbody {
      page-break-inside: auto;
    }
    thead {
      display: table-header-group;
    }
    .no-data {
      color: #999;
      font-style: italic;
      text-align: center;
      padding: 10px;
    }
    .section {
      margin-bottom: 12px;
    }
  </style>
</head>
<body>
  <h1>Service Order: ${stockNumber}</h1>

  <div class="section">
    <div class="summary">
      ${thumbnail ? `<div class="summary-image"><img src="${thumbnail}" alt="Car" onerror="this.style.display='none'" /></div>` : ''}
      <div class="summary-info">
        <div class="summary-col">
          <div class="summary-row"><span class="summary-label">SO #</span><span class="summary-value">${stockNumber}</span></div>
          <div class="summary-row"><span class="summary-label">Status</span><span class="summary-value">${orderStatus}</span></div>
          <div class="summary-row"><span class="summary-label">Mechanic</span><span class="summary-value">${getMechanicName(order.mechanicId)}</span></div>
        </div>
        <div class="summary-col">
          <div class="summary-row"><span class="summary-label">Car</span><span class="summary-value">${carInfo || 'Not assigned'}</span></div>
          ${car.vin ? `<div class="summary-row"><span class="summary-label">VIN</span><span class="summary-value">${car.vin}</span></div>` : ''}
          ${car.mileage ? `<div class="summary-row"><span class="summary-label">Mileage</span><span class="summary-value">${car.mileage}</span></div>` : ''}
        </div>
        <div class="summary-col">
          <div class="summary-row"><span class="summary-label">Customer</span><span class="summary-value">${customerInfo}</span></div>
          ${customer.email ? `<div class="summary-row"><span class="summary-label">Email</span><span class="summary-value">${customer.email}</span></div>` : ''}
          ${customerPhone ? `<div class="summary-row"><span class="summary-label">Phone</span><span class="summary-value">${customerPhone}</span></div>` : ''}
        </div>
      </div>
    </div>
  </div>

  <div class="section">
    <h2>Accounting</h2>
    <table class="accounting-table">
      <tbody>
        ${useSplitModel ? `
        <tr>
          <td class="label">Customer Pays</td>
          <td class="amount">${fmtCurrency(printCustomerAmount)}</td>
        </tr>
        <tr>
          <td class="label">+&nbsp; Internal (Skyway)</td>
          <td class="amount">${fmtCurrency(printInternalAmount)}</td>
        </tr>
        <tr>
          <td class="label"><strong>=&nbsp; Revenue</strong></td>
          <td class="amount"><strong>${fmtCurrency(printRevenue)}</strong></td>
        </tr>
        ` : `
        <tr>
          <td class="label">Revenue</td>
          <td class="amount">${fmtCurrency(printRevenue)}</td>
        </tr>
        `}
        <tr>
          <td class="label">&minus;&nbsp; Labor</td>
          <td class="amount">${fmtCurrency(printLabor)}</td>
        </tr>
        <tr>
          <td class="label">&minus;&nbsp; Purchases</td>
          <td class="amount">${fmtCurrency(printPurchases)}</td>
        </tr>
        <tr class="total-row">
          <td class="label">=&nbsp; ${printProfit >= 0 ? 'Profit' : 'Loss'}</td>
          <td class="amount ${printProfit >= 0 ? 'profit-positive' : 'profit-negative'}">${fmtCurrency(printProfit)}</td>
        </tr>
      </tbody>
    </table>
  </div>

  <div class="section">
    <h2>Accounts Receivable</h2>
    <table class="accounting-table">
      <tbody>
        <tr>
          <td class="label">${useSplitModel ? 'Customer Pays' : 'Revenue'}</td>
          <td class="amount">${fmtCurrency(printBilled)}</td>
        </tr>
        <tr>
          <td class="label">+&nbsp; Sales Tax</td>
          <td class="amount">${fmtCurrency(printSalesTax)}</td>
        </tr>
        <tr>
          <td class="label">=&nbsp; Total</td>
          <td class="amount">${fmtCurrency(printTotal)}</td>
        </tr>
        <tr>
          <td class="label">&minus;&nbsp; Deposits</td>
          <td class="amount">${fmtCurrency(printDeposits)}</td>
        </tr>
        <tr class="total-row">
          <td class="label">=&nbsp; Amount Owed</td>
          <td class="amount ${printAmountOwed > 0 ? 'profit-negative' : 'profit-positive'}">${fmtCurrency(printAmountOwed)}</td>
        </tr>
      </tbody>
    </table>
  </div>

  <div class="section">
    <h2>Deposits</h2>
    <table>
      <thead>
        <tr>
          <th style="width: 110px;">Date</th>
          <th>Memo</th>
          <th style="width: 110px; text-align:right;">Amount</th>
        </tr>
      </thead>
      <tbody>
        ${depositsList}
      </tbody>
    </table>
  </div>

  <div class="section">
    <h2>Services</h2>
    <table>
      <thead>
        <tr>
          <th>Service</th>
          <th style="width: 70px;">Hours</th>
          <th style="width: 110px;">Status</th>
        </tr>
      </thead>
      <tbody>
        ${servicesList}
      </tbody>
    </table>
  </div>

  <div class="section">
    <h2>Parts</h2>
    <table>
      <thead>
        <tr>
          <th>Part</th>
          <th style="width: 100px;">Status</th>
          <th style="width: 90px;">Order Date</th>
          <th style="width: 90px;">Arrival Date</th>
          <th style="width: 80px; text-align:right;">Cost</th>
        </tr>
      </thead>
      <tbody>
        ${partsList}
      </tbody>
    </table>
  </div>

  <div class="section">
    <h2>Subcontracted Work</h2>
    <table>
      <thead>
        <tr>
          <th>Description</th>
          <th style="width: 140px;">Vendor</th>
          <th style="width: 100px;">Status</th>
          <th style="width: 90px; text-align:right;">Cost</th>
        </tr>
      </thead>
      <tbody>
        ${subcontractsList}
      </tbody>
    </table>
  </div>
</body>
</html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    
    // Wait for content to load, then print
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
      }, 250);
    };
  };

  // Delete handler
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const [editingField, setEditingField] = React.useState(null);
  const [pendingStatusChange, setPendingStatusChange] = React.useState(null);
  const [carSelectDialogOpen, setCarSelectDialogOpen] = React.useState(false);
  const [carSearchOptions, setCarSearchOptions] = React.useState([]);
  const [carSearchLoading, setCarSearchLoading] = React.useState(false);
  const [carSearchInput, setCarSearchInput] = React.useState("");
  const [customerSearchOptions, setCustomerSearchOptions] = React.useState([]);
  const [customerSearchLoading, setCustomerSearchLoading] = React.useState(false);
  const [customerSearchInput, setCustomerSearchInput] = React.useState("");
  const [newCustomerModalOpen, setNewCustomerModalOpen] = React.useState(false);
  const [newCustomerInitialName, setNewCustomerInitialName] = React.useState("");

  const handleDeleteServiceOrder = async () => {
    const expenses = order.expenses || [];
    const totalExpenses = expenses.reduce((sum, exp) => sum + (exp.amount || 0), 0);
    
    if (totalExpenses < 0) {
      StateManager.setAlertAndOpen("You cannot delete a service order that has expenses attached to it.", "error");
      setDeleteConfirmOpen(false);
      return;
    }

    try {
      StateManager.setLoading(true);
      await firebase.firestore().doc("orders/"+stockNumber).delete();

      // When the customer is Skyway Classics, the SO's internal allocation is mirrored
      // into a "trade" purchase doc at purchases/{soId} (see AccountingTab.saveAllocation).
      // Delete that corresponding purchase entry so it isn't orphaned.
      const SKYWAY_ID = '9c0d88f5-84f9-454d-833d-a8ced9adad49';
      const customerId = order.customer && (typeof order.customer === 'string' ? order.customer : order.customer.id);
      if (customerId === SKYWAY_ID) {
        await firebase.firestore().doc("purchases/"+stockNumber).delete();
      }

      StateManager.setLoading(false);
      StateManager.setAlertAndOpen("Service order deleted successfully", "success");
      setDeleteConfirmOpen(false);
      
      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error('Error deleting service order:', error);
      StateManager.setLoading(false);
      StateManager.setAlertAndOpen("Error deleting service order", "error");
      setDeleteConfirmOpen(false);
    }
  };

  // Date update handler - optimized to prevent flicker
  const dateUpdate = React.useCallback(async (id, date, closeDialog = false) => {
    // Store previous value for error recovery
    const previousValue = order[id];
    const previousMonth = id === 'complete_date' ? order.month : undefined;
    
    // Close dialog if requested (for date picker)
    if (closeDialog) {
      setEditingField(null);
    }
    
    // Optimistic update
    const update = {[id]: !date ? "" : moment(date).format('YYYY-MM-DD')}
    if(id == "complete_date") update.month = !date ? "" : moment(date).format('YYYY-MM');
    
    // Update state immediately
    setOrder(prevOrder => ({ ...prevOrder, ...update }));
    
    // Save to database in background
    firebase.firestore().doc('orders/'+stockNumber).set(update, {merge:true}).catch(error => {
      console.error('Error updating date:', error);
      // Revert on error
      setOrder(prevOrder => {
        const reverted = {...prevOrder};
        reverted[id] = previousValue;
        if (id === 'complete_date' && previousMonth !== undefined) {
          reverted.month = previousMonth;
        } else if (id === 'complete_date') {
          delete reverted.month;
        }
        return reverted;
      });
      StateManager.setAlertAndOpen('Error updating date', 'error');
    });
  }, [stockNumber, order]);

  // Status update handler - optimized to prevent flicker
  const statusUpdater = React.useCallback(async (value) => {
    if(value === "working") {
      // Store pending status change and open bay selection dialog
      setPendingStatusChange(value);
      setEditingField('Bay');
      return;
    }

    // Soft quality gate: nudge (don't block) when finishing an SO whose quality
    // checklist isn't fully passed. The move still proceeds.
    if (value === 'payment' || value === 'complete') {
      const q = computeQualitySummary(order);
      if (q.total > 0 && !q.allClear) {
        const bits = [];
        if (q.failed) bits.push(`${q.failed} failed`);
        if (q.needsRecheck) bits.push(`${q.needsRecheck} need re-check`);
        StateManager.setAlertAndOpen(
          `Heads up: ${q.passed}/${q.total} services passed quality${bits.length ? ` (${bits.join(', ')})` : ''}. Moving to ${value} anyway.`,
          'warning'
        );
      }
    }

    // Close dialog immediately
    setEditingField(null);
    setPendingStatusChange(null);

    // Store previous values for error recovery
    const previousStatus = order.status;
    const previousStatusTime = order.status_time;

    // Optimistic update
    const update = {status: value, status_time: moment().format("YYYY/MM/DD")};
    setOrder(prevOrder => ({ ...prevOrder, ...update }));
    
    // Save to database in background
    firebase.firestore().doc('orders/'+stockNumber).set(update, {merge: true}).catch(error => {
      console.error('Error updating status:', error);
      // Revert on error
      setOrder(prevOrder => ({ ...prevOrder, status: previousStatus, status_time: previousStatusTime }));
      StateManager.setAlertAndOpen('Error updating status', 'error');
    });
  }, [stockNumber, order.bay, order.status, order.status_time]);

  // Mechanic update handler - optimized to prevent flicker
  const mechanicUpdater = React.useCallback(async (value) => {
    // Close dialog immediately
    setEditingField(null);
    
    // Optimistic update
    setOrder(prevOrder => ({ ...prevOrder, mechanicId: value || null }));
    
    // Save to database in background
    firebase.firestore().doc('orders/'+stockNumber).set({mechanicId: value || null}, {merge: true}).catch(error => {
      console.error('Error updating mechanic:', error);
      // Revert on error
      setOrder(prevOrder => ({ ...prevOrder, mechanicId: order.mechanicId }));
      StateManager.setAlertAndOpen('Error updating mechanic', 'error');
    });
  }, [stockNumber, order.mechanicId]);

  // Sales credit update handler — who gets paid for this order. Optimistic like the
  // handlers above: write in the background, put the old value back if it fails.
  const salesCreditUpdater = React.useCallback((credits) => {
    setEditingField(null);

    const previous = order.sales_credits;
    const previousSource = order.sales_credits_source;
    const update = {
      sales_credits: credits,
      sales_credits_source: CREDIT_SOURCE.MANUAL,
    };
    setOrder(prevOrder => ({ ...prevOrder, ...update }));

    firebase.firestore().doc('orders/'+stockNumber).set(update, {merge: true}).catch(error => {
      console.error('Error updating sales credit:', error);
      setOrder(prevOrder => ({
        ...prevOrder,
        sales_credits: previous,
        sales_credits_source: previousSource,
      }));
      StateManager.setAlertAndOpen('Error updating sales credit', 'error');
    });
  }, [stockNumber, order.sales_credits, order.sales_credits_source]);

  // Bay update handler - optimized to prevent flicker
  const bayUpdater = React.useCallback(async (value) => {
    // Close dialog immediately
    setEditingField(null);
    
    // Check if there's a pending status change (e.g., status to "working" requires bay)
    const pendingStatus = pendingStatusChange;
    setPendingStatusChange(null);
    
    // Optimistic update
    setOrder(prevOrder => ({ ...prevOrder, bay: value || null }));
    
    // Prepare update object
    const update = {bay: value || null};
    
    // If there's a pending status change and bay is now set, include status update
    if (pendingStatus && value) {
      update.status = pendingStatus;
      update.status_time = moment().format("YYYY/MM/DD");
      // Optimistic update for status as well
      setOrder(prevOrder => ({ 
        ...prevOrder, 
        status: pendingStatus, 
        status_time: update.status_time 
      }));
    }
    
    // Save to database in background
    firebase.firestore().doc('orders/'+stockNumber).set(update, {merge: true}).catch(error => {
      console.error('Error updating bay:', error);
      // Revert on error
      setOrder(prevOrder => ({ ...prevOrder, bay: order.bay }));
      if (pendingStatus && value) {
        setOrder(prevOrder => ({ 
          ...prevOrder, 
          status: order.status, 
          status_time: order.status_time 
        }));
      }
      StateManager.setAlertAndOpen('Error updating bay', 'error');
    });
  }, [stockNumber, order.bay, order.status, order.status_time, pendingStatusChange]);

  // Customer update handler - optimized to prevent flicker
  const customerUpdater = React.useCallback(async (customerId) => {
    // Skyway Classics is not a selectable customer — reject any attempt to set it.
    if (constants.isSkywayCustomer(customerId)) {
      StateManager.setAlertAndOpen('Skyway Classics cannot be selected as the customer', 'error');
      return;
    }

    // Close dialog immediately
    setEditingField(null);

    // Nudge: when switching off Skyway customer on a split-model SO with $0 customer
    // billed, offer to set Customer Pays to the current total cost so it isn't left
    // as a fully internal allocation by accident.
    const SKYWAY_ID = '9c0d88f5-84f9-454d-833d-a8ced9adad49';
    const prevCustomerId = order.customer && (typeof order.customer === 'string' ? order.customer : order.customer.id);
    const isSwitchOffSkyway = prevCustomerId === SKYWAY_ID && customerId && customerId !== SKYWAY_ID;
    const splitModel = order.customerPays && order.customerPays.value != null;
    const currentCustomerVal = splitModel ? Number(order.customerPays.value) || 0 : null;
    if (isSwitchOffSkyway && splitModel && currentCustomerVal === 0) {
      const laborCost = (order.services || []).reduce((a, c) => a + (c.cost || 0), 0);
      const purchasesTotal = (order.expenses || [])
        .filter(e => !(e.account === 'trade' && e.id === stockNumber))
        .reduce((a, c) => a + (c.amount || 0), 0);
      const totalCost = Math.round((laborCost + purchasesTotal) * 100) / 100;
      if (totalCost > 0 && window.confirm(
        `Switching to a paying customer.\n\nSet "Customer Pays" to $${totalCost.toFixed(2)} (current Total Cost) and clear the Skyway internal portion?\n\nCancel to leave the allocation as-is.`
      )) {
        const customerPaysUpdate = { type: 'amount', value: totalCost };
        await firebase.firestore().doc('orders/' + stockNumber).set({
          customerPays: customerPaysUpdate,
          internalAmount: 0,
          revenue: totalCost,
        }, { merge: true });
        await firebase.firestore().doc('purchases/' + stockNumber).set({ amount: 0 }, { merge: true });
        setOrder(prev => ({
          ...prev,
          customerPays: customerPaysUpdate,
          internalAmount: 0,
          revenue: totalCost,
        }));
      }
    }

    if (!customerId) {
      // Optimistic update
      setOrder(prev => ({ ...prev, customer: null }));
      
      // Save to database in background
      firebase.firestore().doc('orders/'+stockNumber).set(
        {customer: firebase.firestore.FieldValue.delete()}, 
        {merge: true}
      ).catch(error => {
        console.error('Error removing customer:', error);
        setOrder(prev => ({ ...prev, customer: order.customer }));
        StateManager.setAlertAndOpen('Error removing customer', 'error');
      });
      return;
    }

    if (!constants.isValidCustomerId(customerId)) {
      StateManager.setAlertAndOpen('Customer not found', 'error');
      return;
    }

    // Optimistic update with customer ID
    setOrder(prev => ({ ...prev, customer: { id: customerId } }));
    
    // Fetch customer data and save in background
    try {
      const [customerDoc] = await Promise.all([
        firebase.firestore().doc(`customers/${customerId}`).get(),
        firebase.firestore().doc('orders/'+stockNumber).set(
          {customer: customerId}, 
          {merge: true}
        )
      ]);
      
      if (customerDoc.exists) {
        const customerData = { ...customerDoc.data(), id: customerId };
        customerData.display_name = `${customerData.first_name || ""}${!!customerData.last_name ? " " : ""}${customerData.last_name || ""}`;
        setOrder(prev => ({ ...prev, customer: customerData }));
      }
    } catch (error) {
      console.error('Error selecting customer:', error);
      setOrder(prev => ({ ...prev, customer: order.customer }));
      StateManager.setAlertAndOpen('Error selecting customer', 'error');
    }
  }, [stockNumber, order.customer]);

  const getCustomerDisplayName = () => {
    const customer = order.customer || {};
    return customer && customer.display_name 
      ? customer.display_name 
      : customer && typeof customer === 'object' && customer.first_name
        ? `${customer.first_name || ""}${!!customer.last_name ? " " : ""}${customer.last_name || ""}`
        : "";
  };

  const { car = {} } = order;
  const thumbnail = car.thumbnail || order.thumbnail || null;
  // The car thumbnail can only be set from the SO during the early photo phase:
  // while the car is still before "active" AND its sub_status is at/before
  // "pictures". Once it's live (active+) or past the pictures stage, lock it.
  const canEditThumbnail = (() => {
    const activeIdx = constants.statuses.indexOf('active');
    const picturesIdx = constants.sub_statuses.indexOf('pictures');
    const statusIdx = constants.statuses.indexOf((car.status || '').toLowerCase());
    const subIdx = constants.sub_statuses.indexOf((car.sub_status || '').toLowerCase());
    if (statusIdx !== -1 && statusIdx >= activeIdx) return false; // active or beyond
    if (subIdx > picturesIdx) return false;                       // past the pictures stage
    return true;
  })();
  // Admins can always push an SO from payment to complete, in any P&L scenario
  // (positive revenue with a loss, break-even, or zero revenue). The cron still
  // auto-completes the happy path; this is the manual override for everything else.
  const canMarkComplete = StateManager.isAdmin();
  // "new" appears while the SO is still awaiting review — you graduate it to
  // "estimate" via the button (or this dropdown). Only admins can move an SO
  // back into "new".
  const canMoveToNew = StateManager.isAdmin();
  const activeSelections = constants.makeSelects("order_statuses", undefined, (status) =>
    (status !== "complete" || canMarkComplete) && (status !== "new" || order.status === "new" || canMoveToNew));
  // Only active mechanics are assignable. An SO already assigned to a mechanic who
  // has since been deactivated still lists them, so the current assignment stays
  // visible (and re-selectable) instead of silently reading as unassigned.
  const mechanics = constants.makeSelects("mechanics", {valueKey: "id", labelKey: "name",},
    (mechanic) => mechanic.is_active || mechanic.id === order.mechanicId);
  const baySelections = [
    {value: "", label: "No Bay"},
    ...Array.from({ length: 15 }, (_, i) => ({
      value: String(i + 1),
      label: `Bay ${i + 1}`
    }))
  ];
  const minDate = !StateManager.isAdmin();

  return (
    <ModalStackProvider value={myZ}>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={fullScreen}
      sx={{ zIndex: myZ }}
      PaperProps={{ sx: paperSx }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: `1px solid ${theme.palette.divider}`,
        pb: 1.5
      }}>
        <Typography variant="h6" component="div">
          Service Order: {stockNumber}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title="Refresh">
          <IconButton
            aria-label="refresh"
            onClick={() => setRefreshKey(k => k + 1)}
            sx={{
              color: theme.palette.grey[500],
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="service order tabs"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          {tabs.map((tab, index) => (
            <Tab key={index} label={tab.label} />
          ))}
        </Tabs>
      </Box>

      <DialogContent sx={{ 
        flex: 1, 
        overflow: 'auto',
        p: 0,
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: theme.palette.grey[100],
        },
        '&::-webkit-scrollbar-thumb': {
          background: theme.palette.grey[400],
          borderRadius: '4px',
        },
      }}>
        {/* Profile Section - Inside scrollable area */}
        {!loading && (
          <Box sx={{ px: 3, pt: 3, pb: 2 }}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                bgcolor: 'background.paper',
                p: 4,
              }}
            >
            <Grid container spacing={4} alignItems="center">
              {/* Car Image */}
              <Grid item>
                <Tooltip title={StateManager.isAdmin() && !order.disabled && !readOnly ? "Click to change vehicle" : ""}>
                  <Box
                    onClick={() => {
                      if (StateManager.isAdmin() && !order.disabled && !readOnly) {
                        setCarSelectDialogOpen(true);
                      }
                    }}
                    sx={{
                      position: 'relative',
                      cursor: (StateManager.isAdmin() && !order.disabled && !readOnly) ? 'pointer' : 'default',
                      '&:hover': (StateManager.isAdmin() && !order.disabled) ? {
                        '& .edit-overlay': {
                          opacity: 1,
                        }
                      } : {},
                    }}
                  >
                    {thumbnail ? (
                      <Box
                        component="img"
                        src={thumbnail}
                        alt={car.title || 'Car'}
                        sx={{
                          width: 140,
                          height: 140,
                          objectFit: 'cover',
                          borderRadius: 2,
                          border: `3px solid ${theme.palette.common.white}`,
                          boxShadow: theme.shadows[4],
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <Box
                      sx={{
                        width: 140,
                        height: 140,
                        display: thumbnail ? 'none' : 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: alpha(theme.palette.grey[200], 0.5),
                        borderRadius: 2,
                        border: `3px solid ${theme.palette.common.white}`,
                        boxShadow: theme.shadows[4],
                      }}
                    >
                      <DirectionsCarIcon
                        sx={{
                          fontSize: 60,
                          color: theme.palette.grey[400],
                        }}
                      />
                    </Box>
                    {(StateManager.isAdmin() && !order.disabled && !readOnly) && (
                      <Box
                        className="edit-overlay"
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          bgcolor: alpha(theme.palette.common.black, 0.4),
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          opacity: 0,
                          transition: 'opacity 0.2s',
                          pointerEvents: 'none',
                        }}
                      >
                        <Avatar
                          sx={{
                            bgcolor: alpha(theme.palette.common.white, 0.9),
                            color: theme.palette.primary.main,
                            width: 48,
                            height: 48,
                          }}
                        >
                          <EditIcon />
                        </Avatar>
                      </Box>
                    )}

                    {/* Quick "add car photo" badge — sets the car's thumbnail
                        straight from the SO (great for imageless cars). Only
                        while the car is pre-active and at/before the pictures
                        stage; locked once it's live or further along. */}
                    {!readOnly && (car.stock || car.id) && canEditThumbnail && (
                      <>
                        <Tooltip title={thumbnail ? 'Replace car photo' : 'Add car photo'}>
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); thumbInputRef.current && thumbInputRef.current.click(); }}
                            disabled={uploadingThumb}
                            sx={{
                              position: 'absolute',
                              bottom: -6,
                              right: -6,
                              bgcolor: 'primary.main',
                              color: 'common.white',
                              boxShadow: theme.shadows[3],
                              '&:hover': { bgcolor: 'primary.dark' },
                            }}
                          >
                            {uploadingThumb ? <CircularProgress size={18} sx={{ color: 'common.white' }} /> : <AddAPhotoIcon sx={{ fontSize: 18 }} />}
                          </IconButton>
                        </Tooltip>
                        <input
                          ref={thumbInputRef}
                          type="file"
                          accept="image/*"
                          capture="environment"
                          style={{ display: 'none' }}
                          onChange={handleThumbnailFile}
                        />
                      </>
                    )}
                  </Box>
                </Tooltip>
              </Grid>

              {/* Header Info */}
              <Grid item xs>
                {car.title && (
                  <Box
                    onClick={() => {
                      if (car.stock || car.id) StateManager.openCarModal(car.stock || car.id);
                    }}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      mb: 1,
                      cursor: (car.stock || car.id) ? 'pointer' : 'default',
                      '&:hover': (car.stock || car.id) ? {
                        color: theme.palette.primary.main,
                        '& .launch-icon': {
                          opacity: 1,
                        }
                      } : {},
                      transition: 'color 0.2s',
                    }}
                  >
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 500, 
                        color: 'inherit',
                      }}
                    >
                      {car.title}
                    </Typography>
                    {(car.stock || car.id) && (
                      <LaunchIcon 
                        className="launch-icon"
                        sx={{ 
                          fontSize: 18, 
                          opacity: 0.5,
                          transition: 'opacity 0.2s',
                        }} 
                      />
                    )}
                  </Box>
                )}
                
                {/* Customer Info */}
                {getCustomerDisplayName() && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Chip
                      label={"Customer: "+getCustomerDisplayName()}
                      onClick={(!order.disabled && !readOnly) ? () => setEditingField('Customer') : undefined}
                      icon={<PersonIcon />}
                      sx={{
                        bgcolor: alpha(theme.palette.secondary.main, 0.12),
                        color: theme.palette.secondary.main,
                        fontWeight: 600,
                        height: 28,
                        cursor: (!order.disabled && !readOnly) ? 'pointer' : 'default',
                        '&:hover': (!order.disabled && !readOnly) ? {
                          bgcolor: alpha(theme.palette.secondary.main, 0.2),
                        } : {},
                      }}
                      size="small"
                    />
                    {order.customer && (order.customer.id || (typeof order.customer === 'string' && order.customer)) && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          const customerId = order.customer.id || order.customer;
                          if (customerId) {
                            if (e.ctrlKey || e.metaKey) {
                              window.open(`${window.location.pathname}?customer=${customerId}`, '_blank');
                            } else {
                              StateManager.openCustomerModal(customerId);
                            }
                          }
                        }}
                        sx={{
                          padding: 0.5,
                          '&:hover': {
                            color: theme.palette.primary.main,
                          },
                        }}
                      >
                        <LaunchIcon 
                          sx={{ 
                            fontSize: 16,
                          }} 
                        />
                      </IconButton>
                    )}
                  </Box>
                )}
                
                {/* Chips */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                  {order.status && (
                    <Chip
                      label={`Status: ${order.status.charAt(0).toUpperCase() + order.status.slice(1)}`}
                      onClick={(!order.disabled && !readOnly) ? () => setEditingField('Status') : undefined}
                      sx={{
                        bgcolor: alpha(getStatusColor(order.status), 0.12),
                        color: getStatusColor(order.status),
                        fontWeight: 600,
                        height: 28,
                        cursor: (!order.disabled && !readOnly) ? 'pointer' : 'default',
                        '&:hover': (!order.disabled && !readOnly) ? {
                          bgcolor: alpha(getStatusColor(order.status), 0.2),
                        } : {},
                      }}
                      size="small"
                    />
                  )}
                  {order.documentSends_loaded && signatureSend && (() => {
                    const state = signatureSend.signatureStatus;
                    const signed = state === 'signed';
                    const dead = state === 'cancelled' || state === 'expired';
                    const color = signed ? theme.palette.success.main
                      : dead ? theme.palette.grey[600]
                      : theme.palette.info.main;
                    const stamp = (ts) => (ts && ts.seconds ? moment.unix(ts.seconds).format('MM/DD') : '');
                    const when = signed ? stamp(signatureSend.signedAt) : stamp(signatureSend.sentAt);
                    const doc = (signatureSend.documentType === 'custom' && signatureSend.documentName)
                      || signatureSend.documentLabel || 'Document';
                    const label = signed ? `${doc} Signed${when ? ` ${when}` : ''}`
                      : state === 'partially-signed' ? `${doc} Partially Signed`
                      : dead ? `${doc} Signature ${state === 'expired' ? 'Expired' : 'Cancelled'}`
                      : `${doc} Awaiting Signature${when ? ` — sent ${when}` : ''}`;
                    const canOpen = sendsTabIndex >= 0;
                    return (
                      <Chip
                        icon={signed ? <CheckCircleIcon /> : <DrawOutlinedIcon />}
                        label={label}
                        onClick={canOpen ? () => setActiveTab(sendsTabIndex) : undefined}
                        sx={{
                          bgcolor: alpha(color, 0.12),
                          color,
                          fontWeight: 600,
                          height: 28,
                          cursor: canOpen ? 'pointer' : 'default',
                          '&:hover': canOpen ? { bgcolor: alpha(color, 0.2) } : {},
                          // MUI paints a Chip icon in the default action color
                          // unless told otherwise; the other chips have no icon.
                          '& .MuiChip-icon': { color },
                        }}
                        size="small"
                      />
                    );
                  })()}
                  {order.status === 'new' && !order.disabled && !readOnly && (
                    <Button
                      size="small"
                      variant="contained"
                      color="warning"
                      startIcon={<TrendingFlatIcon />}
                      onClick={() => statusUpdater('estimate')}
                      sx={{ height: 28, fontWeight: 700, textTransform: 'none' }}
                    >
                      Move to Estimate
                    </Button>
                  )}
                  {order.createdBy && (
                    <Chip
                      icon={<PersonIcon />}
                      label={`Created by: ${order.createdByName || constants.userNames[order.createdBy] || resolvedCreator || order.createdBy}`}
                      sx={{
                        bgcolor: alpha(theme.palette.grey[500], 0.12),
                        color: theme.palette.grey[700],
                        fontWeight: 600,
                        height: 28,
                      }}
                      size="small"
                    />
                  )}
                  <Chip
                    label={`Mechanic: ${getMechanicName(order.mechanicId)}`}
                    onClick={(!order.disabled && !readOnly) ? () => setEditingField('Primary Mechanic') : undefined}
                    sx={{
                      bgcolor: alpha(getMechanicColor(order.mechanicId), 0.12),
                      color: getMechanicColor(order.mechanicId),
                      fontWeight: 600,
                      height: 28,
                      cursor: (!order.disabled && !readOnly) ? 'pointer' : 'default',
                      '&:hover': (!order.disabled && !readOnly) ? {
                        bgcolor: alpha(getMechanicColor(order.mechanicId), 0.2),
                      } : {},
                    }}
                    size="small"
                  />
                  <Chip
                    label={order.bay ? `Bay ${order.bay}` : 'No Bay'}
                    onClick={!order.disabled ? () => setEditingField('Bay') : undefined}
                    sx={{
                      bgcolor: alpha(theme.palette.grey[500], 0.12),
                      color: order.bay ? theme.palette.grey[700] : theme.palette.grey[600],
                      fontWeight: 600,
                      height: 28,
                      cursor: !order.disabled ? 'pointer' : 'default',
                      '&:hover': !order.disabled ? {
                        bgcolor: alpha(theme.palette.grey[500], 0.2),
                      } : {},
                    }}
                    size="small"
                  />
                  {order.date && (
                    <Chip
                      icon={<CalendarTodayIcon />}
                      label={`Start: ${moment(order.date.replace(/-/g, "/")).format('MM/DD/YYYY')}`}
                      onClick={(!order.disabled && StateManager.isAdmin() && !readOnly) ? () => setEditingField('Start Date') : undefined}
                      sx={{
                        bgcolor: alpha(theme.palette.info.main, 0.12),
                        color: theme.palette.info.main,
                        fontWeight: 600,
                        height: 28,
                        cursor: (!order.disabled && StateManager.isAdmin() && !readOnly) ? 'pointer' : 'default',
                        '&:hover': (!order.disabled && StateManager.isAdmin() && !readOnly) ? {
                          bgcolor: alpha(theme.palette.info.main, 0.2),
                        } : {},
                      }}
                      size="small"
                    />
                  )}
                  <Chip
                    icon={<CalendarTodayIcon />}
                    label={`Complete: ${order.complete_date ? moment(order.complete_date.replace(/-/g, "/")).format('MM/DD/YYYY') : 'Not Set'}`}
                    onClick={(!order.disabled && StateManager.isAdmin() && !readOnly) ? () => setEditingField('Complete Date') : undefined}
                    sx={{
                      bgcolor: alpha(theme.palette.success.main, 0.12),
                      color: order.complete_date ? theme.palette.success.main : theme.palette.grey[600],
                      fontWeight: 600,
                      height: 28,
                      cursor: (!order.disabled && StateManager.isAdmin() && !readOnly) ? 'pointer' : 'default',
                      '&:hover': (!order.disabled && StateManager.isAdmin() && !readOnly) ? {
                        bgcolor: alpha(theme.palette.success.main, 0.2),
                      } : {},
                    }}
                    size="small"
                  />
                </Box>

                {/* Car shipping status + sales/consignment rep — their own line below the status row */}
                {(car.stock || salesCredits.length || canEditSalesCredit || carSalesRep || car.consign_rep) && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                    {/* Car shipping status (derived from the drop-off load, like the car modal) */}
                    {car.stock && (
                      <ShippingStatusChip stock={car.stock} deal={deal} size="small" sx={{ height: 28, fontWeight: 600 }} />
                    )}
                    {/* Who gets paid for this order. A decision of its own — whoever sold the
                        work earns it, which is often not the rep who sold the car. */}
                    {(salesCredits.length > 0 || canEditSalesCredit) && (
                      <Tooltip
                        title={
                          salesCreditLocked
                            ? 'Locked — this order is included in a generated pay period'
                            : canEditSalesCredit ? 'Click to change who is paid for this order' : ''
                        }
                      >
                        <Chip
                          icon={<PersonIcon />}
                          label={`Sales Credit: ${salesCredits.length ? creditsLabel(salesCredits) : 'Set'}`}
                          onClick={canEditSalesCredit ? () => setEditingField('Sales Credit') : undefined}
                          sx={{
                            bgcolor: alpha(theme.palette.primary.main, 0.12),
                            color: theme.palette.primary.main,
                            fontWeight: 600,
                            height: 28,
                            cursor: canEditSalesCredit ? 'pointer' : 'default',
                            '&:hover': canEditSalesCredit
                              ? { bgcolor: alpha(theme.palette.primary.main, 0.2) }
                              : {},
                          }}
                          size="small"
                        />
                      </Tooltip>
                    )}
                    {/* Who sold the car — context, not credit. Kept next to the consignment
                        rep because both describe the car, not this order's work. */}
                    {carSalesRep && (
                      <Chip
                        icon={<PersonIcon />}
                        label={`Sold By: ${carSalesRep}`}
                        sx={{
                          bgcolor: alpha(theme.palette.info.main, 0.12),
                          color: theme.palette.info.dark,
                          fontWeight: 600,
                          height: 28,
                        }}
                        size="small"
                      />
                    )}
                    {car.consign_rep && (
                      <Chip
                        icon={<PersonIcon />}
                        label={`Consignment Rep: ${car.consign_rep}`}
                        sx={{
                          bgcolor: alpha(theme.palette.info.main, 0.12),
                          color: theme.palette.info.dark,
                          fontWeight: 600,
                          height: 28,
                        }}
                        size="small"
                      />
                    )}
                  </Box>
                )}
              </Grid>
            </Grid>
          </Paper>
        </Box>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: 400 }}>
            <CircularProgress />
          </Box>
        ) : ActiveTabComponent ? (
          <Box sx={{ px: 3, pb: 3 }}>
            <ActiveTabComponent
              order={order}
              stockNumber={stockNumber}
              setOrder={setOrder}
              onClose={onClose}
              hideEstimate={hideEstimate}
            />
          </Box>
        ) : null}
      </DialogContent>

      {/* Action Buttons - Below Tabs */}
      {!loading && (
        <Box sx={{ 
          px: 3, 
          pb: 2, 
          borderTop: `1px solid ${theme.palette.divider}`, 
          pt: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          {StateManager.isAdmin() && !readOnly && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteConfirmOpen(true)}
            >
              Delete Service Order
            </Button>
          )}
          <Box sx={{ display: 'flex', gap: 2, ml: 'auto' }}>
            {!readOnly && (
              <Button
                variant="outlined"
                color="primary"
                startIcon={<PrintIcon />}
                onClick={handlePrint}
              >
                Print
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              onClick={onClose}
            >
              Close
            </Button>
          </Box>
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{ zIndex: childZ }}
      >
        <DialogTitle>Delete Service Order</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete service order <strong>{stockNumber}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleDeleteServiceOrder} 
            color="error" 
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </ConfirmDialog>

      {/* Status/Mechanic/Bay/Date Edit Dialogs */}
      {editingField === 'Status' && (
        <ConfirmDialog
          open={true}
          onClose={() => {
            setEditingField(null);
            setPendingStatusChange(null);
          }}
          maxWidth="xs"
          fullWidth
          sx={{ zIndex: childZ }}
          TransitionProps={{ timeout: 150 }}
          slotProps={{
            backdrop: {
              sx: { transition: 'opacity 0.15s' }
            }
          }}
        >
          <DialogTitle>Change Status</DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {activeSelections.map((option) => (
                <Button
                  key={option.value}
                  variant={order.status === option.value ? 'contained' : 'outlined'}
                  onClick={() => {
                    statusUpdater(option.value);
                  }}
                  fullWidth
                  sx={{ justifyContent: 'flex-start' }}
                >
                  {option.label}
                </Button>
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setEditingField(null);
              setPendingStatusChange(null);
            }}>Cancel</Button>
          </DialogActions>
        </ConfirmDialog>
      )}

      {editingField === 'Sales Credit' && (
        <SalesCreditDialog
          open={true}
          onClose={() => setEditingField(null)}
          onSave={salesCreditUpdater}
          value={salesCredits}
          title="Sales Credit"
          subtitle={`${stockNumber}${order.car && order.car.stock ? ` — ${order.car.stock}` : ''}`}
          helperText="Who earns this service order. Split it when two reps worked it together. This is separate from the rep who sold the car — nobody is paid for the work unless they are credited here."
        />
      )}

      {editingField === 'Primary Mechanic' && (
        <ConfirmDialog
          open={true}
          onClose={() => setEditingField(null)}
          maxWidth="xs"
          fullWidth
          sx={{ zIndex: childZ }}
          TransitionProps={{ timeout: 150 }}
          slotProps={{
            backdrop: {
              sx: { transition: 'opacity 0.15s' }
            }
          }}
        >
          <DialogTitle>Change Primary Mechanic</DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button
                variant={!order.mechanicId ? 'contained' : 'outlined'}
                onClick={() => {
                  mechanicUpdater(null);
                }}
                fullWidth
                sx={{ justifyContent: 'flex-start' }}
              >
                Not assigned
              </Button>
              {mechanics.map((option) => (
                <Button
                  key={option.value}
                  variant={order.mechanicId === option.value ? 'contained' : 'outlined'}
                  onClick={() => {
                    mechanicUpdater(option.value);
                  }}
                  fullWidth
                  sx={{ justifyContent: 'flex-start' }}
                >
                  {option.label}
                </Button>
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditingField(null)}>Cancel</Button>
          </DialogActions>
        </ConfirmDialog>
      )}

      {editingField === 'Bay' && (
        <ConfirmDialog
          open={true}
          onClose={() => {
            setEditingField(null);
            setPendingStatusChange(null);
          }}
          maxWidth="xs"
          fullWidth
          sx={{ zIndex: childZ }}
          TransitionProps={{ timeout: 150 }}
          slotProps={{
            backdrop: {
              sx: { transition: 'opacity 0.15s' }
            }
          }}
        >
          <DialogTitle>Change Bay</DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {baySelections.map((option) => (
                <Button
                  key={option.value}
                  variant={order.bay === option.value ? 'contained' : 'outlined'}
                  onClick={() => {
                    bayUpdater(option.value);
                  }}
                  fullWidth
                  sx={{ justifyContent: 'flex-start' }}
                >
                  {option.label}
                </Button>
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setEditingField(null);
              setPendingStatusChange(null);
            }}>Cancel</Button>
          </DialogActions>
        </ConfirmDialog>
      )}

      {(editingField === 'Start Date' || editingField === 'Complete Date') && (
        <ConfirmDialog
          open={true}
          onClose={() => setEditingField(null)}
          maxWidth="xs"
          sx={{ zIndex: childZ }}
          fullWidth
          TransitionProps={{ timeout: 150 }}
          slotProps={{
            backdrop: {
              sx: { transition: 'opacity 0.15s' }
            }
          }}
        >
          <DialogTitle>{editingField}</DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                label={editingField}
                value={editingField === 'Start Date' 
                  ? (order.date ? moment(order.date.replace(/-/g, "/")) : null)
                  : (order.complete_date ? moment(order.complete_date.replace(/-/g, "/")) : null)
                }
                onChange={(newValue) => {
                  if (newValue && newValue.isValid()) {
                    if (editingField === 'Start Date') {
                      dateUpdate('date', newValue.toDate(), true);
                    } else {
                      dateUpdate('complete_date', newValue.toDate(), true);
                    }
                  } else {
                    if (editingField === 'Start Date') {
                      dateUpdate('date', null, true);
                    } else {
                      dateUpdate('complete_date', null, true);
                    }
                  }
                }}
                disabled={order.disabled || !StateManager.isAdmin()}
                minDate={minDate ? moment() : undefined}
                slotProps={{
                  textField: {
                    fullWidth: true,
                  },
                  popper: {
                    sx: { zIndex: childZ + 1 },
                  },
                }}
              />
            </LocalizationProvider>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditingField(null)}>Cancel</Button>
          </DialogActions>
        </ConfirmDialog>
      )}

      {/* Customer Selection Dialog */}
      {editingField === 'Customer' && (
        <ConfirmDialog
          open={true}
          onClose={() => setEditingField(null)}
          maxWidth="sm"
          sx={{ zIndex: childZ }}
          fullWidth
          TransitionProps={{ timeout: 150 }}
          slotProps={{
            backdrop: {
              sx: { transition: 'opacity 0.15s' }
            }
          }}
        >
          <DialogTitle>Select Customer</DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Autocomplete
              freeSolo
              slotProps={{ popper: { sx: { zIndex: childZ + 1 } } }}
              options={customerSearchOptions}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                return option.label || "";
              }}
              inputValue={customerSearchInput}
              onInputChange={(event, newInputValue, reason) => {
                setCustomerSearchInput(newInputValue);
                if (reason === 'input' && newInputValue.length > 0) {
                  setCustomerSearchLoading(true);
                  const index = algolia.client.initIndex('customers');
                  index.search(newInputValue).then(({ hits }) => {
                    const customerOptions = hits
                      // Skyway Classics is not a selectable customer.
                      .filter(hit => !constants.isSkywayCustomer(hit.objectID))
                      .map(hit => ({
                        ...hit,
                        label: `${hit.first_name || ""} ${hit.last_name || ""}`.trim(),
                        id: hit.objectID
                      }));

                    const createNewOption = {
                      id: 'create-new',
                      label: `Create "${newInputValue}"`,
                      isNew: true,
                      query: newInputValue
                    };

                    setCustomerSearchOptions([createNewOption, ...customerOptions]);
                    setCustomerSearchLoading(false);
                  }).catch((error) => {
                    console.error('Error searching customers:', error);
                    setCustomerSearchOptions([]);
                    setCustomerSearchLoading(false);
                  });
                } else {
                  setCustomerSearchOptions([]);
                }
              }}
              onChange={async (event, newValue, reason) => {
                if (reason === 'selectOption') {
                  if (newValue && newValue.isNew) {
                    setNewCustomerInitialName(newValue.query || "");
                    setNewCustomerModalOpen(true);
                    return;
                  }
                  customerUpdater(newValue ? newValue.id : null);
                } else if (reason === 'clear') {
                  customerUpdater(null);
                }
              }}
              loading={customerSearchLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search or create customer..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {customerSearchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id || option.label}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    {option.isNew ? (
                      <>
                        <AddIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                        <Typography sx={{ color: 'primary.main', fontWeight: 600 }}>
                          {option.label}
                        </Typography>
                      </>
                    ) : (
                      <>
                        <PersonIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                        <Typography>{option.label}</Typography>
                      </>
                    )}
                  </Box>
                </Box>
              )}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditingField(null)}>Cancel</Button>
          </DialogActions>
        </ConfirmDialog>
      )}

      {/* Car Selection Dialog */}
      <ConfirmDialog
        open={carSelectDialogOpen}
        onClose={() => setCarSelectDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{ zIndex: childZ }}
        TransitionProps={{ timeout: 150 }}
        slotProps={{
          backdrop: {
            sx: { transition: 'opacity 0.15s' }
          }
        }}
      >
        <DialogTitle>Select Vehicle</DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Autocomplete
            freeSolo
            slotProps={{ popper: { sx: { zIndex: childZ + 1 } } }}
            options={carSearchOptions}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              return option.label || "";
            }}
            inputValue={carSearchInput}
            onInputChange={(event, newInputValue, reason) => {
              setCarSearchInput(newInputValue);
              if (reason === 'input' && newInputValue.length > 0) {
                setCarSearchLoading(true);
                const index = algolia.client.initIndex('cars');
                index.search(newInputValue).then(({ hits }) => {
                  const sortedHits = hits.sort((a, b) => {
                    const stockA = a.stock ? parseInt(a.stock.replace(/\D/g, ''), 10) || 0 : 0;
                    const stockB = b.stock ? parseInt(b.stock.replace(/\D/g, ''), 10) || 0 : 0;
                    return stockB - stockA;
                  });

                  const carOptions = sortedHits.map(hit => ({
                    ...hit,
                    label: `${hit.stock || ""} - ${hit.year || ""} ${hit.make || ""} ${hit.model || ""}`.trim(),
                    id: hit.objectID || hit.stock
                  }));

                  setCarSearchOptions(carOptions);
                  setCarSearchLoading(false);
                }).catch((error) => {
                  console.error('Error searching cars:', error);
                  setCarSearchOptions([]);
                  setCarSearchLoading(false);
                });
              } else {
                setCarSearchOptions([]);
              }
            }}
            onChange={async (event, newValue, reason) => {
              if (reason === 'selectOption' && newValue) {
                setCarSearchLoading(true);
                try {
                  const carId = newValue.objectID || newValue.id;
                  const db = firebase.firestore();
                  
                  const carDoc = await db.doc(`cars/${carId}`).get();
                  if (carDoc.exists) {
                    const carData = { ...carDoc.data(), id: carId };
                    carData.title = `${carData.stock || ""} - ${carData.year || ""} ${carData.make || ""} ${carData.model || ""}`;
                    
                    await db.doc('orders/'+stockNumber).set(
                      {car: carId, thumbnail: carData.thumbnail || null}, 
                      {merge: true}
                    );
                    
                    setOrder(prev => ({ 
                      ...prev, 
                      car: carData,
                      thumbnail: carData.thumbnail || null
                    }));
                    setCarSelectDialogOpen(false);
                  }
                } catch (error) {
                  console.error('Error selecting car:', error);
                } finally {
                  setCarSearchLoading(false);
                }
              }
            }}
            loading={carSearchLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search for vehicle..."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {carSearchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.id || option.label}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                  {option.thumbnail ? (
                    <Box
                      component="img"
                      src={option.thumbnail}
                      alt={option.label}
                      sx={{
                        width: 40,
                        height: 40,
                        objectFit: 'cover',
                        borderRadius: 1,
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <DirectionsCarIcon sx={{ color: 'text.secondary', fontSize: 24 }} />
                  )}
                  <Typography>{option.label}</Typography>
                </Box>
              </Box>
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCarSelectDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </ConfirmDialog>
      <NewCustomerModal
        open={newCustomerModalOpen}
        onClose={() => setNewCustomerModalOpen(false)}
        stockNumber={stockNumber}
        type="customer"
        initialName={newCustomerInitialName}
        onSaved={(customer) => {
          setNewCustomerModalOpen(false);
          setEditingField(null);
          customerUpdater(customer.id);
        }}
      />
    </Dialog>
    </ModalStackProvider>
  );
}

